/**
 * RegisterStep1 - User credentials input
 * Step 1 of 3: Email, Password, Username
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/v2/ui/Button';
import { Input } from '@/components/v2/ui/Input';

interface Props {
  onComplete: (data: { email: string; password: string; username: string }) => void;
  initialData: { email: string; password: string; username: string };
}

interface Errors {
  email?: string;
  password?: string;
  username?: string;
}

// Password validation helper - counts character types
const countCharacterTypes = (password: string): number => {
  let types = 0;
  if (/[a-z]/.test(password)) types++; // lowercase
  if (/[A-Z]/.test(password)) types++; // uppercase
  if (/[0-9]/.test(password)) types++; // numbers
  if (/[^a-zA-Z0-9]/.test(password)) types++; // special characters
  return types;
};

export function RegisterStep1({ onComplete, initialData }: Props) {
  const { t } = useTranslation();
  const [email, setEmail] = useState(initialData.email);
  const [password, setPassword] = useState(initialData.password);
  const [username, setUsername] = useState(initialData.username);
  const [errors, setErrors] = useState<Errors>({});

  const validate = (): boolean => {
    const newErrors: Errors = {};

    // Email validation
    if (!email) {
      newErrors.email = 'Email ist erforderlich';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      newErrors.email = t('auth.invalidEmail');
    }

    // Password validation (Supabase rules: min 8 chars + 2 character types)
    if (!password) {
      newErrors.password = 'Passwort ist erforderlich';
    } else if (password.length < 8) {
      newErrors.password = 'Passwort muss mindestens 8 Zeichen lang sein';
    } else if (countCharacterTypes(password) < 2) {
      newErrors.password = 'Passwort muss mindestens 2 verschiedene Zeichentypen enthalten (Buchstaben, Zahlen, Sonderzeichen)';
    }

    // Username validation
    if (!username) {
      newErrors.username = 'Benutzername ist erforderlich';
    } else if (username.length < 3) {
      newErrors.username = 'Benutzername muss mindestens 3 Zeichen lang sein';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validate()) {
      onComplete({ email, password, username });
    }
  };

  return (
    <form onSubmit={handleSubmit} className="register-step1 space-y-4">
      <Input
        label="Email"
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        error={errors.email}
        required
        autoComplete="email"
        placeholder="name@beispiel.de"
      />

      <Input
        label="Passwort"
        type="password"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        error={errors.password}
        helperText="Mindestens 8 Zeichen und 2 verschiedene Zeichentypen (z.B. Buchstaben + Zahlen)"
        required
        autoComplete="new-password"
        placeholder="••••••••"
      />

      <Input
        label="Benutzername"
        type="text"
        value={username}
        onChange={(e) => setUsername(e.target.value)}
        error={errors.username}
        required
        autoComplete="username"
        placeholder="Max Mustermann"
      />

      <Button type="submit" variant="primary" className="w-full mt-6">
        {t('common.next')}
      </Button>
    </form>
  );
}
